import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setForm({ current_password: '', new_password: '', confirm: '' });
    setError('');
    setSuccess(false);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (form.new_password !== form.confirm) {
      setError('New password and confirmation do not match');
      return;
    }
    if (form.new_password.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: form.current_password,
          new_password: form.new_password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to change password');
      setSuccess(true);
      setTimeout(() => {
        onOpenChange(false);
        reset();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-tge-navy">Change Password</DialogTitle>
          <DialogDescription>
            Enter your current password and a new password of at least 8 characters.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl p-5 text-center">
            ✓ Password updated successfully.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-xl text-sm" data-testid="cp-error">
                {error}
              </div>
            )}

            <LabelledInput
              label="Current password"
              type="password"
              value={form.current_password}
              onChange={(v) => setForm({ ...form, current_password: v })}
              testid="cp-current"
              autoComplete="current-password"
            />
            <LabelledInput
              label="New password"
              type="password"
              value={form.new_password}
              onChange={(v) => setForm({ ...form, new_password: v })}
              testid="cp-new"
              autoComplete="new-password"
            />
            <LabelledInput
              label="Confirm new password"
              type="password"
              value={form.confirm}
              onChange={(v) => setForm({ ...form, confirm: v })}
              testid="cp-confirm"
              autoComplete="new-password"
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="cp-submit"
              className="w-full h-11 rounded-full bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold transition disabled:opacity-60"
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LabelledInput({ label, type, value, onChange, testid, autoComplete }) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="block text-[13px] font-medium text-tge-navy">{label}</span>
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="text-[11px] text-tge-muted hover:text-tge-navy inline-flex items-center gap-1"
            data-testid={`${testid}-toggle`}
            tabIndex={-1}
          >
            {show ? <EyeOff size={12} /> : <Eye size={12} />} {show ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
      <input
        type={isPassword && show ? 'text' : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full h-11 px-4 rounded-xl bg-tge-cream-soft border border-transparent focus:border-tge-gold focus:bg-white focus:outline-none focus:ring-2 focus:ring-tge-gold/30 transition text-[15px]"
      />
    </label>
  );
}
